/**
 * useAuth Hook
 *
 * React hook for authentication state management.
 */

import { useState, useEffect, useCallback } from 'react';
import authService, { AuthUser, LoginCredentials } from '../services/authService';
import { ApiError, isApiError, getErrorMessage } from '../services/apiClient';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface UseAuthReturn extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  hasRole: (role: AuthUser['role']) => boolean;
  hasAnyRole: (roles: AuthUser['role'][]) => boolean;
  isAdmin: boolean;
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: authService.getUser(),
    isAuthenticated: authService.isAuthenticated(),
    isLoading: false,
    error: null,
  });

  // Listen for logout events from apiClient
  useEffect(() => {
    const handleLogout = () => {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await authService.login(credentials);
      setState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const message = isApiError(error)
        ? error.message
        : getErrorMessage(error);

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));

      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      await authService.logout();
    } finally {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authService.isAuthenticated()) return;

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const user = await authService.getCurrentUser();
      setState((prev) => ({
        ...prev,
        user,
        isLoading: false,
      }));
    } catch (error) {
      // If refresh fails, user might need to re-login
      console.warn('Failed to refresh user:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const hasRole = useCallback(
    (role: AuthUser['role']) => {
      return state.user?.role === role;
    },
    [state.user]
  );

  const hasAnyRole = useCallback(
    (roles: AuthUser['role'][]) => {
      return state.user ? roles.includes(state.user.role) : false;
    },
    [state.user]
  );

  const isAdmin = state.user?.role === 'ADMIN' || state.user?.is_superuser === true;

  return {
    ...state,
    login,
    logout,
    clearError,
    refreshUser,
    hasRole,
    hasAnyRole,
    isAdmin,
  };
}

export default useAuth;
